/**
 * Walkthrough assessment coverage — partial-submit scope for scoring/report.
 *
 * Assessed statuses: finding | clear (surveyor disposition recorded).
 * Not assessed: pending (never dispositioned) and skipped (explicit skip / out of scope).
 * Not-assessed items MUST be excluded from scoring denominators and report findings,
 * with explicit coverage language on the report.
 */

import {
  SURVEY_WALKTHROUGH,
  totalInspectionItems,
  type AreaWalkthrough,
} from './surveyGuide'

export type WalkthroughStepStatus = 'pending' | 'finding' | 'skipped' | 'clear'

export interface WalkthroughStepLike {
  status?: WalkthroughStepStatus | string
}

export interface AssessmentAreaCoverage {
  area: string
  areaLabel: string
  totalItems: number
  assessedItems: number
  notAssessedItems: number
  findingItems: number
  clearItems: number
  skippedItems: number
  pendingItems: number
  fullyAssessed: boolean
  anyAssessed: boolean
}

export interface AssessmentCoverage {
  totalItems: number
  assessedItems: number
  notAssessedItems: number
  findingItems: number
  clearItems: number
  skippedItems: number
  pendingItems: number
  isPartial: boolean
  isFullyAssessed: boolean
  assessedAreas: AssessmentAreaCoverage[]
  notAssessedAreas: AssessmentAreaCoverage[]
  /** Human-readable scope lines for confirm / report */
  scopeSummary: string
  notAssessedDisclaimer: string
  confirmPrompt: string
}

export function isAssessedStatus(status: string | undefined | null): boolean {
  return status === 'finding' || status === 'clear'
}

export function isNotAssessedStatus(status: string | undefined | null): boolean {
  return !isAssessedStatus(status)
}

function emptyCoverage(totalItems: number): AssessmentCoverage {
  return {
    totalItems,
    assessedItems: 0,
    notAssessedItems: totalItems,
    findingItems: 0,
    clearItems: 0,
    skippedItems: 0,
    pendingItems: totalItems,
    isPartial: totalItems > 0,
    isFullyAssessed: totalItems === 0,
    assessedAreas: [],
    notAssessedAreas: [],
    scopeSummary:
      totalItems === 0
        ? 'No inspection items are defined for this walkthrough.'
        : 'No areas have been assessed yet.',
    notAssessedDisclaimer:
      'Areas that were not assessed are not included in the report findings or scoring methodology.',
    confirmPrompt:
      'Only the areas you assessed will be included. Unassessed areas will be excluded from scoring and report findings. Are you sure you want to proceed?',
  }
}

export function computeWalkthroughAssessmentCoverage(
  stepStates: Record<string, WalkthroughStepLike> | null | undefined,
  walkthrough: AreaWalkthrough[] = SURVEY_WALKTHROUGH,
): AssessmentCoverage {
  const totalItems = walkthrough.reduce(
    (sum, area) => sum + area.sections.reduce((s, sec) => s + sec.items.length, 0),
    0,
  ) || totalInspectionItems()

  if (!stepStates || totalItems === 0) {
    return emptyCoverage(totalItems)
  }

  const areaRows: AssessmentAreaCoverage[] = []
  let assessedItems = 0
  let findingItems = 0
  let clearItems = 0
  let skippedItems = 0
  let pendingItems = 0

  walkthrough.forEach((area, ai) => {
    let areaTotal = 0
    let areaAssessed = 0
    let areaFinding = 0
    let areaClear = 0
    let areaSkipped = 0
    let areaPending = 0

    area.sections.forEach((section, si) => {
      section.items.forEach((_, ii) => {
        areaTotal += 1
        const key = `${ai}-${si}-${ii}`
        const status = stepStates[key]?.status
        if (status === 'finding') {
          areaFinding += 1
          areaAssessed += 1
        } else if (status === 'clear') {
          areaClear += 1
          areaAssessed += 1
        } else if (status === 'skipped') {
          areaSkipped += 1
        } else {
          areaPending += 1
        }
      })
    })

    assessedItems += areaAssessed
    findingItems += areaFinding
    clearItems += areaClear
    skippedItems += areaSkipped
    pendingItems += areaPending

    areaRows.push({
      area: area.area,
      areaLabel: area.areaLabel,
      totalItems: areaTotal,
      assessedItems: areaAssessed,
      notAssessedItems: areaTotal - areaAssessed,
      findingItems: areaFinding,
      clearItems: areaClear,
      skippedItems: areaSkipped,
      pendingItems: areaPending,
      fullyAssessed: areaAssessed === areaTotal && areaTotal > 0,
      anyAssessed: areaAssessed > 0,
    })
  })

  const notAssessedItems = totalItems - assessedItems
  const isPartial = notAssessedItems > 0
  const isFullyAssessed = notAssessedItems === 0 && totalItems > 0

  const assessedAreas = areaRows.filter((a) => a.anyAssessed)
  const notAssessedAreas = areaRows.filter((a) => a.notAssessedItems > 0)

  const assessedLabels = assessedAreas.map((a) => a.areaLabel)
  const notAssessedLabels = notAssessedAreas
    .filter((a) => !a.anyAssessed)
    .map((a) => a.areaLabel)
  const partiallyAssessedLabels = notAssessedAreas
    .filter((a) => a.anyAssessed)
    .map((a) => `${a.areaLabel} (${a.assessedItems}/${a.totalItems})`)

  const scopeParts: string[] = []
  if (assessedLabels.length > 0) {
    scopeParts.push(
      `Assessed: ${assessedItems} of ${totalItems} items`
      + (assessedLabels.length ? ` across ${assessedLabels.join(', ')}` : ''),
    )
  } else {
    scopeParts.push('No items have been assessed yet.')
  }
  if (partiallyAssessedLabels.length > 0) {
    scopeParts.push(`Partially assessed areas: ${partiallyAssessedLabels.join('; ')}.`)
  }
  if (notAssessedLabels.length > 0) {
    scopeParts.push(`Not assessed (excluded): ${notAssessedLabels.join(', ')}.`)
  }

  const notAssessedDisclaimer = isPartial
    ? 'The following areas were not assessed (or were only partially assessed) and are not included in the report findings or scoring methodology. Scores reflect only assessed items.'
    : 'All walkthrough inspection items were assessed and are included in scoring and report findings.'

  const confirmPrompt = isPartial
    ? `Only these areas were assessed (${assessedItems} of ${totalItems} items). Unassessed items will be removed from the scoring methodology and will not appear as report findings. Are you sure you want to proceed?`
    : 'All items have been assessed. Proceed to complete the survey and generate the report?'

  return {
    totalItems,
    assessedItems,
    notAssessedItems,
    findingItems,
    clearItems,
    skippedItems,
    pendingItems,
    isPartial,
    isFullyAssessed,
    assessedAreas,
    notAssessedAreas,
    scopeSummary: scopeParts.join(' '),
    notAssessedDisclaimer,
    confirmPrompt,
  }
}

/** Scoring rule: only assessed dispositions contribute to denominators. */
export function scoringEligibleStatuses(): readonly WalkthroughStepStatus[] {
  return ['finding', 'clear'] as const
}

export function scoringExcludedStatuses(): readonly WalkthroughStepStatus[] {
  return ['pending', 'skipped'] as const
}
